/**
 * User List component with pagination and search
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import { apiClient } from '../../apiClient';
import '../../styles/themes.css'; // Import theme styles

const UserList = () => {
  const {
    users,
    loadUsers,
    loadUserDetail,
    bulkUpdateUsers,
    updateUserVisibility
  } = useAdmin();

  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  const limit = 20;

  useEffect(() => {
    loadUsersData();
  }, [page, search]);

  const loadUsersData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await loadUsers(page, limit, search);
      setTotalUsers(response.total);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleUserClick = async (userId) => {
    try {
      await loadUserDetail(userId);
    } catch (err) {
      setError('Failed to load user details: ' + err.message);
    }
  };

  const handleSelectUser = (userId) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(user => user.id)));
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.size === 0) return;

    try {
      setLoading(true);
      const userIds = Array.from(selectedUsers);

      // Handle admin role changes
      if (bulkAction === 'make_admin' || bulkAction === 'remove_admin') {
        let updates = {};
        if (bulkAction === 'make_admin') {
          updates.is_admin = true;
        } else if (bulkAction === 'remove_admin') {
          updates.is_admin = false;
        }

        await bulkUpdateUsers(userIds, updates);
      }
      // Handle leaderboard visibility changes
      else if (bulkAction === 'add_to_leaderboard' || bulkAction === 'remove_from_leaderboard') {
        const showOnLeaderboard = bulkAction === 'add_to_leaderboard';

        // Use bulk update instead of individual calls
        await bulkUpdateUsers(userIds, { show_on_leaderboard: showOnLeaderboard });
      }
      // Handle user deletion
      else if (bulkAction === 'delete') {
        // Get user emails for confirmation
        const selectedUserData = users.filter(user => userIds.includes(user.id));
        const userEmails = selectedUserData.map(user => user.email).join(', ');

        const confirmed = window.confirm(
          `Are you sure you want to permanently delete ${userIds.length} user(s)?\n\n` +
          `Users: ${userEmails}\n\n` +
          'This action cannot be undone and will remove:\n' +
          '- User profiles and authentication\n' +
          '- All task submissions and code\n' +
          '- UI preferences and settings\n' +
          '- Associated audit logs\n\n' +
          'This is a complete hard delete operation.'
        );

        if (!confirmed) {
          setLoading(false);
          return;
        }

        // Delete each user individually
        const deletePromises = userIds.map(userId =>
          apiClient.delete(`/api/admin/users/${userId}`)
        );

        await Promise.all(deletePromises);
      }

      setSelectedUsers(new Set());
      setBulkAction('');
      await loadUsersData(); // Refresh the list
    } catch (err) {
      setError('Bulk action failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: Tasks solved count is now provided directly by the backend in the user list
  // No need for separate API calls - data comes from efficient SQL JOIN query



  // Task counts are now included directly in the user data from the backend

  const totalPages = Math.ceil(totalUsers / limit);

  return (
    <div className="themed-background" style={{ padding: '1rem', minHeight: '100vh' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <h2 style={{ color: 'var(--color-background-text)' }}>Users ({totalUsers})</h2>

        {/* Search */}
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={handleSearch}
          className="themed-input"
          style={{ width: '300px' }}
        />
      </div>

      {/* Bulk Actions */}
      {selectedUsers.size > 0 && (
        <div className="themed-surface" style={{
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span>{selectedUsers.size} users selected</span>
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            className="themed-input"
            style={{ padding: '0.25rem' }}
          >
            <option value="">Select action...</option>
            <option value="make_admin">Make Admin</option>
            <option value="remove_admin">Remove Admin</option>
            <option value="add_to_leaderboard">Add to Leaderboard</option>
            <option value="remove_from_leaderboard">Remove from Leaderboard</option>
            <option value="delete">Delete Users</option>
          </select>
          <button
            onClick={handleBulkAction}
            disabled={!bulkAction}
            className="themed-button"
            style={{
              cursor: bulkAction ? 'pointer' : 'not-allowed',
              opacity: bulkAction ? 1 : 0.6
            }}
          >
            Apply
          </button>
        </div>
      )}

      {error && (
        <div className="status-error themed-surface" style={{
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid var(--color-error)'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--color-background-text)'
        }}>
          Loading users...
        </div>
      ) : (
        <>
          {/* User Table */}
          <table className="themed-table">
            <thead>
              <tr>
                <th
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={users.length > 0 && selectedUsers.size === users.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Email</th>
                <th>Name</th>
                <th>Admin</th>
                <th>Tasks Solved</th>
                <th>Leaderboard Visible</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr
                  key={user.id}
                  onClick={() => handleUserClick(user.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectUser(user.id);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectUser(user.id);
                      }}
                    />
                  </td>
                  <td>{user.email}</td>
                  <td>{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '-'}</td>
                  <td>
                    <span className={user.is_admin ? 'status-success' : 'status-warning'} style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      backgroundColor: user.is_admin ? 'var(--color-success)' : 'var(--color-warning)',
                      color: 'var(--color-background-text)',
                      opacity: 0.9
                    }}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: '600' }}>
                    {user.tasks_solved || 0}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      backgroundColor: user.show_on_leaderboard ? 'var(--color-success)' : 'var(--color-warning)',
                      color: 'white',
                      opacity: 0.9
                    }}>
                      {user.show_on_leaderboard ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '1rem'
            }}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>

              <span className="themed-surface" style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                border: '1px solid var(--border-color)'
              }}>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  opacity: page === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserList;